import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Loader2, X, ExternalLink, ImageIcon, PlayCircle, Play } from "lucide-react";
import { fetchHistoricalMedia } from "@/lib/api";
import { isRTL } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import MediaSlideshow from "@/components/MediaSlideshow";

export default function HistoricalMedia({ snapshotId, meta, t, language = "English", onClose }) {
  const rtl = isRTL(language);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [slideshowOpen, setSlideshowOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchHistoricalMedia(snapshotId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load media"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [snapshotId]);

  const images = data?.images || [];
  const pathe = data?.pathe || {};
  const year = data?.meta?.year || "";
  const decade = data?.meta?.decade || "";
  const provider = pathe.provider || "youtube";
  const sourceLabel = pathe.source_label || "YouTube · Newsreels";
  // British Pathé's YouTube archive is heavily weighted toward the 1910–1975
  // "newsreel era" — searches for later dates almost always return blank
  // pages. Only surface the Pathé cards for anglosphere regions inside that
  // window; other regions get their domestic archive queries instead.
  const patheYearNum = parseInt(year, 10);
  const showPathe = provider === "pathe" && !!patheYearNum && patheYearNum >= 1910 && patheYearNum <= 1975;

  return (
    <div
      className={`fixed inset-0 z-50 bg-ink/70 flex items-start sm:items-center justify-center p-2 sm:p-4 ${rtl ? "tc-arabic" : ""}`}
      onClick={onClose}
      dir={rtl ? "rtl" : "ltr"}
      data-testid="media-modal"
    >
      <div
        className="bg-paper-surface border-2 border-ink shadow-hard max-w-5xl w-full max-h-[95vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-ink sticky top-0 bg-paper-surface z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Film size={20} />
            <div className="min-w-0">
              <h3 className="font-headline text-lg sm:text-xl uppercase font-bold leading-none truncate">{t.mediaTitle}</h3>
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1 truncate">
                {meta?.region?.flag} {meta?.region?.name} · {year} {decade ? `· ${decade}` : ""}
              </div>
            </div>
          </div>
          <button
            data-testid="media-close-btn"
            onClick={onClose}
            className="font-mono text-xs uppercase tracking-widest border-2 border-ink px-2 py-1 hover:bg-paper-alt shrink-0"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          <div className="font-body italic text-sm text-ink-secondary">{t.mediaHint}</div>

          {loading && (
            <div className="text-center py-10" data-testid="media-loading">
              <Loader2 size={28} className="animate-spin mx-auto text-press-red" />
              <div className="mt-3 font-mono uppercase tracking-widest text-xs text-ink-muted">{t.mediaLoading}</div>
            </div>
          )}

          {!loading && (
            <>
              {/* Newsreels — British Pathé (1910–1975) + YouTube */}
              <section data-testid="media-newsreels">
                <div className="flex items-center gap-2 border-b-2 border-ink pb-2 mb-3">
                  <PlayCircle size={16} />
                  <h4 className="font-mono uppercase tracking-[0.2em] text-xs sm:text-sm">— {t.mediaNewsreels} —</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink">
                  {showPathe && (
                    <>
                      <NewsreelCard
                        href={pathe.primary_url}
                        onClick={() => trackEvent("media_pathe_click", { snapshot_id: snapshotId, kind: "primary", year })}
                        label={pathe?.labels?.primary || "Newsreel"}
                        cta={t.mediaWatchPathe}
                        source="British Pathé Archive"
                        testId="pathe-primary"
                      />
                      <NewsreelCard
                        href={pathe.year_url}
                        onClick={() => trackEvent("media_pathe_click", { snapshot_id: snapshotId, kind: "year", year })}
                        label={pathe?.labels?.year || year}
                        cta={t.mediaWatchPathe}
                        source="British Pathé Archive"
                        testId="pathe-year"
                      />
                    </>
                  )}
                  {provider === "regional" && (
                    <>
                      <NewsreelCard
                        href={pathe.primary_url}
                        onClick={() => trackEvent("media_archive_click", { snapshot_id: snapshotId, kind: "primary", year, provider })}
                        label={pathe?.labels?.primary || sourceLabel}
                        cta={t.mediaWatchYoutube}
                        source={sourceLabel}
                        testId="archive-primary"
                      />
                      <NewsreelCard
                        href={pathe.year_url}
                        onClick={() => trackEvent("media_archive_click", { snapshot_id: snapshotId, kind: "year", year, provider })}
                        label={pathe?.labels?.year || year}
                        cta={t.mediaWatchYoutube}
                        source={sourceLabel}
                        testId="archive-year"
                      />
                    </>
                  )}
                  <NewsreelCard
                    href={pathe.youtube_url}
                    onClick={() => trackEvent("media_youtube_click", { snapshot_id: snapshotId, kind: "primary" })}
                    label={pathe?.labels?.primary || "Newsreel"}
                    cta={t.mediaWatchYoutube}
                    source="YouTube · Newsreels"
                    testId="youtube-primary"
                  />
                  <NewsreelCard
                    href={pathe.youtube_year_url}
                    onClick={() => trackEvent("media_youtube_click", { snapshot_id: snapshotId, kind: "year" })}
                    label={year ? `${year} archive` : "Archive"}
                    cta={t.mediaWatchYoutube}
                    source="YouTube · Newsreels"
                    testId="youtube-year"
                  />
                </div>
              </section>

              {/* Photographs — Wikipedia Commons */}
              <section data-testid="media-photos">
                <div className="flex items-center gap-2 border-b-2 border-ink pb-2 mb-3">
                  <ImageIcon size={16} />
                  <h4 className="font-mono uppercase tracking-[0.2em] text-xs sm:text-sm">— {t.mediaPhotos} —</h4>
                </div>

                {images.length === 0 ? (
                  <div className="font-body italic text-ink-muted text-sm py-4" data-testid="media-no-images">
                    {t.mediaNoImages}
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        trackEvent("slideshow_open", { snapshot_id: snapshotId, count: images.length });
                        setSlideshowOpen(true);
                      }}
                      className="mb-4 w-full sm:w-auto inline-flex items-center gap-2 bg-press-red text-paper font-mono uppercase tracking-widest text-xs px-4 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover"
                      style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
                      data-testid="slideshow-open-btn"
                    >
                      <Play size={14} /> {t.mediaSlideshow}
                      <span className="font-mono text-[10px] uppercase tracking-widest opacity-80 border-l-2 border-paper/60 pl-2 ml-1 hidden sm:inline">
                        {images.length} photos
                      </span>
                    </button>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {images.map((img, i) => (
                      <button
                        key={img.full_url + i}
                        onClick={() => {
                          trackEvent("media_photo_open", { snapshot_id: snapshotId, index: i });
                          setLightbox(img);
                        }}
                        className="border-2 border-ink bg-paper-alt overflow-hidden text-left shadow-hard-sm hover:shadow-hard-hover"
                        style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
                        data-testid={`media-photo-${i}`}
                      >
                        <div className="aspect-[4/3] bg-ink/10 overflow-hidden">
                          <img
                            src={img.thumb_url}
                            alt={img.title}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-2">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted line-clamp-2 leading-tight">
                            {img.title.replace(/\.(jpg|jpeg|png|tif|gif)$/i, "")}
                          </div>
                          {img.artist && (
                            <div className="font-mono text-[9px] text-ink-muted mt-1 truncate">
                              © {img.artist}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                    </div>
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-ink/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="media-lightbox"
        >
          <div className="max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.thumb_url}
              alt={lightbox.title}
              referrerPolicy="no-referrer"
              className="max-h-[70vh] w-auto mx-auto object-contain border-2 border-paper"
            />
            <div className="bg-paper-surface border-2 border-paper mt-3 p-4">
              <div className="font-headline text-base sm:text-lg font-bold leading-tight">
                {lightbox.title.replace(/\.(jpg|jpeg|png|tif|gif)$/i, "")}
              </div>
              {lightbox.description && (
                <div className="font-body text-sm text-ink-secondary mt-2 line-clamp-4">{lightbox.description}</div>
              )}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-widest">
                {lightbox.artist && (<div><span className="text-ink-muted">{t.mediaCredit}: </span>{lightbox.artist}</div>)}
                {lightbox.license && (<div><span className="text-ink-muted">{t.mediaLicense}: </span>{lightbox.license}</div>)}
                {lightbox.date && (<div><span className="text-ink-muted">Date: </span>{lightbox.date}</div>)}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <a
                  href={lightbox.descriptor_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("media_photo_source_click", { snapshot_id: snapshotId })}
                  className="inline-flex items-center gap-1 border-2 border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-widest bg-paper-alt hover:bg-ink hover:text-paper"
                  data-testid="lightbox-source-link"
                >
                  {t.mediaOpenSource} <ExternalLink size={12} />
                </a>
                <button
                  onClick={() => setLightbox(null)}
                  className="ml-auto inline-flex items-center gap-1 border-2 border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-widest bg-paper-surface"
                  data-testid="lightbox-close-btn"
                >
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {slideshowOpen && (
        <MediaSlideshow
          snapshotId={snapshotId}
          images={images}
          meta={meta}
          t={t}
          language={language}
          onClose={() => setSlideshowOpen(false)}
        />
      )}
    </div>
  );
}

function NewsreelCard({ href, onClick, label, cta, source, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="border-r-2 border-b-2 border-ink bg-paper-alt hover:bg-ink hover:text-paper p-4 transition-colors group flex flex-col gap-2"
      data-testid={`newsreel-${testId}`}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted group-hover:text-paper/70">
        <PlayCircle size={14} /> {source}
      </div>
      <div className="font-headline text-base sm:text-lg font-bold leading-tight">{label}</div>
      <div className="mt-auto pt-1 font-mono text-[10px] uppercase tracking-widest text-press-red inline-flex items-center gap-1">
        {cta} <ExternalLink size={10} />
      </div>
    </a>
  );
}
